/**
 * Leitor mínimo de .xlsx, sem dependência externa.
 *
 * Um .xlsx é um ZIP com XML dentro. Só precisamos de três coisas: a tabela de
 * strings compartilhadas, o XML da primeira planilha, e as células de cada
 * linha. Isso evita trazer uma biblioteca de ~400kB para ler um relatório de
 * oito colunas.
 */

const ZIP_EOCD_SIGNATURE = 0x06054b50;
const ZIP_CENTRAL_SIGNATURE = 0x02014b50;
const DEFLATE = 8;
const STORED = 0;

export class XlsxError extends Error {}

interface ZipEntry {
  name: string;
  method: number;
  offset: number;
  compressedSize: number;
}

function readCentralDirectory(view: DataView): ZipEntry[] {
  // O EOCD fica no fim do arquivo, depois de um comentário de tamanho variável.
  let eocd = -1;
  const minStart = Math.max(0, view.byteLength - 66_000);
  for (let i = view.byteLength - 22; i >= minStart; i--) {
    if (view.getUint32(i, true) === ZIP_EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new XlsxError('Arquivo não é um .xlsx válido (ZIP sem diretório central).');

  const entryCount = view.getUint16(eocd + 10, true);
  let pointer = view.getUint32(eocd + 16, true);

  const entries: ZipEntry[] = [];
  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(pointer, true) !== ZIP_CENTRAL_SIGNATURE) break;

    const method = view.getUint16(pointer + 10, true);
    const compressedSize = view.getUint32(pointer + 20, true);
    const nameLength = view.getUint16(pointer + 28, true);
    const extraLength = view.getUint16(pointer + 30, true);
    const commentLength = view.getUint16(pointer + 32, true);
    const offset = view.getUint32(pointer + 42, true);

    const nameBytes = new Uint8Array(view.buffer, view.byteOffset + pointer + 46, nameLength);
    entries.push({
      name: new TextDecoder().decode(nameBytes),
      method,
      offset,
      compressedSize,
    });

    pointer += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new XlsxError(
      'Seu navegador não suporta descompactar .xlsx. Exporte o relatório da B3 em CSV.'
    );
  }
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(
    new DecompressionStream('deflate-raw')
  );
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readEntry(buffer: ArrayBuffer, view: DataView, entry: ZipEntry): Promise<string> {
  // O cabeçalho local repete nome e extra com tamanhos próprios.
  const nameLength = view.getUint16(entry.offset + 26, true);
  const extraLength = view.getUint16(entry.offset + 28, true);
  const start = entry.offset + 30 + nameLength + extraLength;
  const raw = new Uint8Array(buffer, start, entry.compressedSize);

  if (entry.method === STORED) return new TextDecoder().decode(raw);
  if (entry.method === DEFLATE) return new TextDecoder().decode(await inflate(raw));
  throw new XlsxError(`Compressão não suportada no .xlsx (método ${entry.method}).`);
}

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

function unescapeXml(text: string): string {
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-z]+);/g, (match, code: string) => {
    if (code.startsWith('#x') || code.startsWith('#X')) {
      return String.fromCodePoint(parseInt(code.slice(2), 16));
    }
    if (code.startsWith('#')) return String.fromCodePoint(parseInt(code.slice(1), 10));
    return ENTITIES[code] ?? match;
  });
}

/** Junta todos os <t> de um bloco — texto rico vem quebrado em vários. */
function textOf(fragment: string): string {
  let result = '';
  const matcher = /<t(?:\s[^>]*)?>([\s\S]*?)<\/t>|<t(?:\s[^>]*)?\/>/g;
  let match: RegExpExecArray | null;
  while ((match = matcher.exec(fragment)) !== null) {
    result += unescapeXml(match[1] ?? '');
  }
  return result;
}

function parseSharedStrings(xml: string): string[] {
  const strings: string[] = [];
  const matcher = /<si(?:\s[^>]*)?>([\s\S]*?)<\/si>|<si(?:\s[^>]*)?\/>/g;
  let match: RegExpExecArray | null;
  while ((match = matcher.exec(xml)) !== null) {
    strings.push(textOf(match[1] ?? ''));
  }
  return strings;
}

/** "BC12" -> 54 (índice da coluna, base 0). */
function columnIndex(reference: string): number {
  const letters = /^[A-Z]+/.exec(reference)?.[0] ?? 'A';
  let index = 0;
  for (const char of letters) {
    index = index * 26 + (char.charCodeAt(0) - 64);
  }
  return index - 1;
}

function parseSheet(xml: string, shared: string[]): string[][] {
  const rows: string[][] = [];
  const rowMatcher = /<row(?:\s[^>]*)?>([\s\S]*?)<\/row>|<row(?:\s[^>]*)?\/>/g;
  let rowMatch: RegExpExecArray | null;

  while ((rowMatch = rowMatcher.exec(xml)) !== null) {
    const cells: string[] = [];
    const body = rowMatch[1] ?? '';
    const cellMatcher = /<c\s([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g;
    let cellMatch: RegExpExecArray | null;

    while ((cellMatch = cellMatcher.exec(body)) !== null) {
      const attributes = cellMatch[1] ?? '';
      const content = cellMatch[2] ?? '';
      const reference = /r="([A-Z]+\d+)"/.exec(attributes)?.[1] ?? '';
      const type = /t="([^"]+)"/.exec(attributes)?.[1] ?? 'n';

      let value = '';
      if (type === 's') {
        const index = Number(/<v>([\s\S]*?)<\/v>/.exec(content)?.[1] ?? '');
        value = shared[index] ?? '';
      } else if (type === 'inlineStr') {
        value = textOf(content);
      } else {
        value = unescapeXml(/<v>([\s\S]*?)<\/v>/.exec(content)?.[1] ?? '');
      }

      const target = reference ? columnIndex(reference) : cells.length;
      while (cells.length < target) cells.push('');
      cells[target] = value;
    }
    rows.push(cells);
  }
  return rows;
}

/** Lê a primeira planilha do arquivo e devolve as linhas como texto. */
export async function readXlsxRows(file: ArrayBuffer): Promise<string[][]> {
  const view = new DataView(file);
  const entries = readCentralDirectory(view);

  const sheet =
    entries.find((e) => e.name === 'xl/worksheets/sheet1.xml') ??
    entries.find((e) => /^xl\/worksheets\/.*\.xml$/.test(e.name));
  if (!sheet) throw new XlsxError('Planilha não encontrada dentro do arquivo.');

  const sharedEntry = entries.find((e) => e.name === 'xl/sharedStrings.xml');
  const shared = sharedEntry
    ? parseSharedStrings(await readEntry(file, view, sharedEntry))
    : [];

  return parseSheet(await readEntry(file, view, sheet), shared);
}
